import { ComposedChart, Line, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { useGet } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Activity, CalendarRange } from "lucide-react";

const LEVEL_COLOR = { "0.01%": "#10B981", "0.1%": "#34D399", "1%": "#F59E0B", "5%": "#FBBF24" };
const hhmm = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
const day = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const pTxt = (p) => (p == null ? "" : p < 0.0001 ? "p < 0.0001" : `p = ${p.toPrecision(2)}`);

function SigChip({ r, level, p, testId }) {
  const color = r == null || r >= 0 ? "#9CA3AF" : level === "0.01%" ? "#10B981" : level ? "#F59E0B" : "#EF4444";
  const text = r == null ? "—" : r >= 0 ? "not negative" : level ? `significant at ${level}` : "not significant";
  return (
    <div data-testid={testId} className="glass rounded-xl px-3 py-2 min-w-0">
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num">Significance</div>
      <div className="text-xs font-mono-num font-semibold leading-snug" style={{ color }}>{text}<br /><span className="text-[#9CA3AF]">{pTxt(p)}</span></div>
    </div>
  );
}

function Stat({ label, value, color = "#D1D5DB", testId }) {
  return (
    <div data-testid={testId} className="glass rounded-xl px-3 py-2 min-w-0">
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
      <div className="text-xs font-mono-num font-semibold leading-snug" style={{ color }}>{value}</div>
    </div>
  );
}

export function DxyGoldIntraday({ refreshKey }) {
  const { data: d, error } = useGet("/market-drivers/dxy-gold/intraday", refreshKey);
  if (error) return null;
  return (
    <section data-testid="dxy-gold-intraday" className="glass rounded-3xl p-5 flex flex-col gap-3">
      <div>
        <div className="font-display font-semibold flex items-center gap-2"><Activity size={15} className="text-[#F59E0B]" /> {d?.market_closed ? "Last session" : "Today's session"} · 5-minute moves</div>
        <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">Daily correlation test · refreshes every 5 min{d ? ` · since ${hhmm(d.session_start)} · ${d.bars} bars` : ""}</div>
      </div>
      {!d ? <div className="h-40 grid place-items-center text-xs text-[#6B7280] font-mono-num">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Correlation (today)" value={d.r ?? "—"} color={d.r <= -0.3 ? "#10B981" : d.r < 0 ? "#F59E0B" : "#EF4444"} testId="intraday-corr" />
            <SigChip r={d.r} level={d.level} p={d.p} testId="intraday-significance" />
            <Stat label="Opposite bars" value={d.opposite_pct != null ? `${d.opposite_pct}%` : "—"} color={d.opposite_pct >= 55 ? "#10B981" : "#9CA3AF"} testId="intraday-opposite" />
            <Stat label="Change today" value={<><span className="text-[#F59E0B]">Gold {d.gold_change_pct > 0 ? "+" : ""}{d.gold_change_pct}%</span><br /><span className="text-[#6366F1]">DXY {d.dxy_change_pct > 0 ? "+" : ""}{d.dxy_change_pct}%</span></>} testId="intraday-change" />
          </div>
          <div className="h-44 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.series} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                <XAxis dataKey="t" tickFormatter={hhmm} tick={{ fill: "#6B7280", fontSize: 10 }} minTickGap={40} axisLine={false} tickLine={false} />
                <YAxis yAxisId="gold" domain={["auto", "auto"]} tick={{ fill: "#F59E0B", fontSize: 10 }} width={46} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v)} />
                <YAxis yAxisId="dxy" orientation="right" domain={["auto", "auto"]} tick={{ fill: "#818CF8", fontSize: 10 }} width={44} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} labelFormatter={hhmm} />
                <Line yAxisId="gold" type="monotone" dataKey="gold" name="Gold" stroke="#F59E0B" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line yAxisId="dxy" type="monotone" dataKey="dxy" name="DXY" stroke="#6366F1" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p data-testid="intraday-summary" className="text-[11px] text-[#9CA3AF] leading-relaxed">{d.summary} Early in the session there are few bars, so treat the verdict as provisional until 50+ bars.</p>
        </>
      )}
    </section>
  );
}

function RollTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="glass rounded-lg px-3 py-2 text-[11px] font-mono-num">
      <div className="text-[#9CA3AF]">{day(p.date)}</div>
      <div style={{ color: p.level ? LEVEL_COLOR[p.level] : "#EF4444" }}>r = {p.r} · {p.level ? `significant at ${p.level}` : "not significant"}</div>
      <div className="text-[#6B7280]">{pTxt(p.p)}</div>
    </div>
  );
}

export function DxyGoldRolling({ refreshKey }) {
  const { data: d, error } = useGet("/market-drivers/dxy-gold/rolling?window=30", refreshKey);
  if (error) return null;
  return (
    <section data-testid="dxy-gold-rolling" className="glass rounded-3xl p-5 flex flex-col gap-3">
      <div>
        <div className="font-display font-semibold flex items-center gap-2"><CalendarRange size={15} className="text-[#F59E0B]" /> Rolling 30-day correlation · past year</div>
        <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">Recomputed daily · bar colour = significance of that day's 30-day window</div>
      </div>
      {!d ? <div className="h-40 grid place-items-center text-xs text-[#6B7280] font-mono-num">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Latest 30-day r" value={d.latest.r} color={d.latest.r <= -0.3 ? "#10B981" : d.latest.r < 0 ? "#F59E0B" : "#EF4444"} testId="rolling-latest" />
            <SigChip r={d.latest.r} level={d.latest.level} p={d.latest.p} testId="rolling-significance" />
            <Stat label="Days significant (≤5%)" value={`${d.significant_pct}%`} color="#34D399" testId="rolling-sig-pct" />
            <Stat label="Days at 0.01%" value={`${d.strong_pct}%`} color="#10B981" testId="rolling-strong-pct" />
          </div>
          <div className="h-44 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.series} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                <XAxis dataKey="date" tickFormatter={day} tick={{ fill: "#6B7280", fontSize: 10 }} minTickGap={40} axisLine={false} tickLine={false} />
                <YAxis domain={[-1, 1]} ticks={[-1, -0.5, 0, 0.5, 1]} tick={{ fill: "#6B7280", fontSize: 10 }} width={36} axisLine={false} tickLine={false} />
                <Tooltip content={<RollTip />} cursor={{ fill: "#ffffff10" }} />
                <ReferenceLine y={0} stroke="#ffffff33" />
                <ReferenceLine y={-0.3} stroke="#10B98155" strokeDasharray="3 3" label={{ value: "−0.3", fill: "#10B981", fontSize: 9, position: "insideLeft" }} />
                <Bar dataKey="r" isAnimationActive={false}>
                  {d.series.map((p) => <Cell key={p.date} fill={p.level ? LEVEL_COLOR[p.level] : p.r < 0 ? "#EF4444" : "#6B7280"} />)}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-3 text-[10px] font-mono-num text-[#9CA3AF]">
            <span><i className="inline-block w-3 h-2 bg-[#10B981] align-middle mr-1" />0.01%</span>
            <span><i className="inline-block w-3 h-2 bg-[#34D399] align-middle mr-1" />0.1%</span>
            <span><i className="inline-block w-3 h-2 bg-[#F59E0B] align-middle mr-1" />1%</span>
            <span><i className="inline-block w-3 h-2 bg-[#FBBF24] align-middle mr-1" />5%</span>
            <span><i className="inline-block w-3 h-2 bg-[#EF4444] align-middle mr-1" />negative, not significant</span>
            <span><i className="inline-block w-3 h-2 bg-[#6B7280] align-middle mr-1" />positive</span>
          </div>
          <p data-testid="rolling-summary" className="text-[11px] text-[#9CA3AF] leading-relaxed">{d.summary}</p>
        </>
      )}
    </section>
  );
}
